import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResourceRequest } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiRequestOptions, TypedResourceRequest } from '../Interfaces/api.interface';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly _http = inject(HttpClient);

  get<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this.buildRequest<T>(resource, 'GET', options);
  }

  post<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this.buildRequest<T>(resource, 'POST', options);
  }

  put<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this.buildRequest<T>(resource, 'PUT', options);
  }

  patch<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this.buildRequest<T>(resource, 'PATCH', options);
  }

  delete<T>(resource: string, options: ApiRequestOptions = {}): TypedResourceRequest<T> {
    return this.buildRequest<T>(resource, 'DELETE', options);
  }

  run<T>(request: HttpResourceRequest): Observable<T> {
    const method = (request.method ?? 'GET') as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    const url = request.url as string;
    const options = {
      body: request.body,
      params: request.params,
      headers: request.headers as HttpHeaders | Record<string, string | string[]> | undefined,
      responseType: 'json' as const,
      withCredentials: request.withCredentials,
    };
    return this._http.request<T>(method, url, options).pipe(map((body) => body as T));
  }

  runAndForget(request: HttpResourceRequest): void {
    this.run(request).subscribe({ error: () => undefined });
  }

  private buildRequest<T>(
    resource: string,
    method: HttpResourceRequest['method'],
    options: ApiRequestOptions
  ): TypedResourceRequest<T> {
    const headers = this.buildHeaders(options);
    const cleanResource = resource.startsWith('/') ? resource.substring(1) : resource;
    return {
      url: `${environment.api.host}/${cleanResource}`,
      method,
      params: options.params,
      body: options.body,
      headers,
      withCredentials: options.withCredentials,
    } as TypedResourceRequest<T>;
  }

  private buildHeaders(options: ApiRequestOptions): HttpHeaders | Record<string, string | string[]> {
    let headers: HttpHeaders | Record<string, string | string[]> = options.headers ?? new HttpHeaders();

    if (options.skipLoading) {
      headers = this.appendHeader(headers, 'skip-loading', 'true');
    }

    if (options.skipError) {
      headers = this.appendHeader(headers, 'skip-error', 'true');
    }

    return headers;
  }

  private appendHeader(
    headers: HttpHeaders | Record<string, string | string[]>,
    name: string,
    value: string,
  ): HttpHeaders | Record<string, string | string[]> {
    return headers instanceof HttpHeaders
      ? headers.append(name, value)
      : { ...headers, [name]: value };
  }
}
